import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface TPAyar {
  id: string;
  islem_tipi: string;
  hesap_tipi: 'yuzde' | 'sabit';
  kazanim_aktif: boolean;
  kazanim_orani: number;
  harcama_aktif: boolean;
  harcama_max_orani: number;
}

const ETIKETLER: Record<string, string> = {
  bakiye_yukleme: 'Bakiye Yükleme',
  ders_hakki_satin_alma: 'Ders Hakkı Satın Alma',
  derse_katilim: 'Derse Katılım',
  milonga_bileti: 'Milonga Bileti',
  pratik_bileti: 'Pratik Bileti',
  festival_bileti: 'Festival Bileti',
};

const ACIKLAMALAR: Record<string, string> = {
  bakiye_yukleme: 'Öğrenci bakiyesine gerçek para yüklendiğinde (nakit/EFT/kredi kartı) TP kazandırır.',
  ders_hakki_satin_alma: 'Ders hakkı paketi satın alınırken biriken TP kullanılarak indirim uygulanabilir.',
  derse_katilim: 'Öğrenci derse "katıldı" olarak işaretlendiğinde sabit miktarda TP kazandırır (devam motivasyonu).',
  milonga_bileti: 'Milonga bileti satışında (öğrenci eşleştirilmişse) TP kazanımı ve TP ile ödeme.',
  pratik_bileti: 'Pratik bileti satışında (öğrenci eşleştirilmişse) TP kazanımı ve TP ile ödeme.',
  festival_bileti: 'Festival bileti satışında (öğrenci eşleştirilmişse) TP kazanımı ve TP ile ödeme.',
};

export default function TPAyarlari() {
  usePageTitle('TP Ayarları');
  const { token } = useAuth() as any;
  const { isDark } = useTheme();
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const card = isDark ? '#1a1a1a' : 'white';
  const border = isDark ? '#2a2a2a' : '#eee';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';
  const inp: React.CSSProperties = { width: 90, padding: '6px 8px', border: `1px solid ${border}`, borderRadius: 7, fontSize: 14, boxSizing: 'border-box', outline: 'none', background: isDark ? '#111' : 'white', color: textPrimary };

  const [ayarlar, setAyarlar] = useState<TPAyar[]>([]);
  const [tpBasinaTl, setTpBasinaTl] = useState<number>(1);
  const [gecerlilikGun, setGecerlilikGun] = useState<string>('');
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediyor, setKaydediyor] = useState<string | null>(null);

  async function yukle() {
    setYukleniyor(true);
    const r = await fetch(`${BASE_URL}/api/tp/ayarlar`, { headers: h });
    const veri = await r.json();
    setAyarlar(veri.ayarlar || []);
    setTpBasinaTl(parseFloat(veri.genel?.tp_basina_tl ?? 1));
    setGecerlilikGun(veri.genel?.gecerlilik_gun != null ? String(veri.genel.gecerlilik_gun) : '');
    setYukleniyor(false);
  }

  useEffect(() => { yukle(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function alanGuncelle(islem_tipi: string, alan: keyof TPAyar, deger: any) {
    setAyarlar(prev => prev.map(a => a.islem_tipi === islem_tipi ? { ...a, [alan]: deger } : a));
  }

  async function kaydet(a: TPAyar) {
    setKaydediyor(a.islem_tipi);
    await fetch(`${BASE_URL}/api/tp/ayarlar/${a.islem_tipi}`, {
      method: 'PUT', headers: h,
      body: JSON.stringify({
        kazanim_aktif: a.kazanim_aktif, kazanim_orani: Number(a.kazanim_orani),
        harcama_aktif: a.harcama_aktif, harcama_max_orani: Number(a.harcama_max_orani),
      }),
    });
    setKaydediyor(null);
  }

  async function genelKaydet() {
    setKaydediyor('__genel');
    await fetch(`${BASE_URL}/api/tp/genel-ayar`, {
      method: 'PUT', headers: h,
      body: JSON.stringify({
        tp_basina_tl: Number(tpBasinaTl),
        gecerlilik_gun: gecerlilikGun.trim() === '' ? null : Number(gecerlilikGun),
      }),
    });
    setKaydediyor(null);
  }

  return (
    <div style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textPrimary }}>TP Ayarları</h2>
        <p style={{ margin: '4px 0 0', color: textSec, fontSize: 13 }}>
          Tango Points sadakat programı — kazanım ve harcama kurallarını buradan yönetin
        </p>
      </div>

      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>1 TP =</span>
          <input style={inp} type="number" step="0.01" value={tpBasinaTl} onChange={e => setTpBasinaTl(Number(e.target.value))} />
          <span style={{ fontSize: 14, color: textPrimary }}>TL</span>

          <span style={{ width: 1, height: 24, background: border, margin: '0 4px' }} />

          <span style={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>Geçerlilik süresi:</span>
          <input style={inp} type="number" step="1" min={0} placeholder="Süresiz" value={gecerlilikGun}
            onChange={e => setGecerlilikGun(e.target.value)} />
          <span style={{ fontSize: 14, color: textPrimary }}>gün</span>

          <button onClick={genelKaydet} disabled={kaydediyor === '__genel'}
            style={{ marginLeft: 'auto', background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {kaydediyor === '__genel' ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
        <div style={{ fontSize: 12, color: textSec, marginTop: 10 }}>
          Kazanılan TP, belirtilen gün içinde kullanılmazsa otomatik olarak sıfırlanır (her gece 04:00'te kontrol edilir). Boş bırakılırsa TP hiç süresi dolmaz.
        </div>
      </div>

      {yukleniyor ? (
        <p style={{ color: textMuted }}>Yükleniyor…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {ayarlar.map(a => (
            <div key={a.islem_tipi} style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>{ETIKETLER[a.islem_tipi] || a.islem_tipi}</div>
                  <div style={{ fontSize: 12, color: textSec, marginTop: 2, maxWidth: 480 }}>{ACIKLAMALAR[a.islem_tipi]}</div>
                </div>
                <button onClick={() => kaydet(a)} disabled={kaydediyor === a.islem_tipi}
                  style={{ background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                  {kaydediyor === a.islem_tipi ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: textPrimary, marginBottom: 6 }}>
                    <input type="checkbox" checked={a.kazanim_aktif} onChange={e => alanGuncelle(a.islem_tipi, 'kazanim_aktif', e.target.checked)} />
                    Kazanım aktif
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input style={inp} type="number" step="0.1" value={a.kazanim_orani} disabled={!a.kazanim_aktif}
                      onChange={e => alanGuncelle(a.islem_tipi, 'kazanim_orani', e.target.value)} />
                    <span style={{ fontSize: 12, color: textSec }}>{a.hesap_tipi === 'sabit' ? 'TP / işlem' : '% (tutarın)'}</span>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: textPrimary, marginBottom: 6 }}>
                    <input type="checkbox" checked={a.harcama_aktif} onChange={e => alanGuncelle(a.islem_tipi, 'harcama_aktif', e.target.checked)} />
                    TP ile ödeme kabul edilsin
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input style={inp} type="number" step="1" min={0} max={100} value={a.harcama_max_orani} disabled={!a.harcama_aktif}
                      onChange={e => alanGuncelle(a.islem_tipi, 'harcama_max_orani', e.target.value)} />
                    <span style={{ fontSize: 12, color: textSec }}>% max (tutarın en fazla bu kadarı TP ile ödenebilir)</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
